<template>
  <div :data-view-id="uniqueTitle" :unique-title="uniqueTitle">
    <ViewTitle :title="title" />
    <b-form @submit="onSubmit" :id="uniqueTitle" novalidate>
      <template v-for="(comp, index) in this.internalFormComponents" :key="comp.uniqueTitle">
        <div v-if="comp.needsFieldSpacing" class="mb-4">
          <component
            :is="comp"
            v-if="comp"
            v-bind:formComponents="comp.formComponents"
            v-bind:uniqueTitle="comp.uniqueTitle"
            v-bind:placeholder="comp.placeholder"
            v-bind:fieldLabel="comp.fieldLabel"
            v-bind:description="comp.description"
            v-bind:checkedDescription="comp.checkedDescription"
            v-bind:choices="comp.choices"
            v-bind:displayStyle="comp.displayStyle"
            v-bind:selectionMode="comp.selectionMode"
            v-bind:durationUnit="comp.durationUnit"
            v-bind:allowImageUrl="comp.allowImageUrl"
            v-bind:dateOnly="comp.dateOnly"
          />
          <FormFieldDescription :description="comp.description" :description-id="comp.description ? comp.uniqueTitle + '-description' : ''" />
        </div>
        <template v-else>
          <component
            :is="comp"
            v-if="comp"
            v-bind:formComponents="comp.formComponents"
            v-bind:uniqueTitle="comp.uniqueTitle"
            v-bind:placeholder="comp.placeholder"
            v-bind:fieldLabel="comp.fieldLabel"
            v-bind:description="comp.description"
            v-bind:checkedDescription="comp.checkedDescription"
            v-bind:choices="comp.choices"
            v-bind:displayStyle="comp.displayStyle"
            v-bind:selectionMode="comp.selectionMode"
            v-bind:durationUnit="comp.durationUnit"
            v-bind:allowImageUrl="comp.allowImageUrl"
            v-bind:dateOnly="comp.dateOnly"
          />
          <FormFieldDescription :description="comp.description" :description-id="comp.description ? comp.uniqueTitle + '-description' : ''" />
        </template>
      </template>
      <b-button type="submit" variant="primary" :class="this.submittedClass">{{submitText}}</b-button>
      <div v-if="liveValidationError || submitError" role="alert" class="text-danger mt-2">{{ liveValidationError || submitError }}</div>
    </b-form>
    <div v-if="submissionMessage" role="status" class="submission-message" style="margin-top: 12px; padding: 12px; border: 1px solid #9ad12b; border-radius: 4px; color: #9ad12b; background: rgba(154, 209, 43, 0.08);">
      {{ submissionMessage }}
    </div>
  </div>
</template>

<script>


  /*
  Related Form Control Components
  See also:

  <b-form-input> Textual and text-like inputs
  <b-form-textarea> Text area inputs
  <b-form-checkbox> Checkbox Inputs

  <b-form-select> Select input
  <b-form-radio> Radio Inputs
  <b-form-file> File Input
  <b-form-group> Form input wrapper to generate form-groups that support labels, help text and feedback
  <b-button> Buttons
  <b-input-group> Form inputs with add-ons

  Helper components
  <b-form-row> create grid rows and columns with tighter margins
  <b-form-text> Help text blocks for inputs
  <b-form-invalid-feedback> Invalid feedback text blocks for input invalid states
  <b-form-valid-feedback> Valid feedback text blocks for input valid states
  */

  import { markRaw, nextTick, reactive, provide } from 'vue'
  import _ from 'lodash'
  import $ from 'jquery'
  import axios from 'axios'
  import FormGroup from './inputs/formgroup.vue'
  import FormRow from './inputs/formrow.vue'
  import { formControlMap, applyFormControlProps } from './inputs/form-controls'
  import ViewTitle from './ViewTitle.vue'
  import FormFieldDescription from './FormFieldDescription.vue'
  import { evaluateFormFormatting, formattingToStyle, evaluateSubmissionRules, evaluateDefaultValueRules } from '../lib/client-rule-evaluator'
  import { appendLoggedInUserQuery, loggedInUserFieldLists } from '../../../shared/logged-in-user-default.js'
  import { submissionRedirectPath } from '../../../shared/submission-redirect'
  import { pageHostUniqueTitle } from '../lib/page-host'
  import { pushAppPath } from '../lib/push-app-path'
import { isEmptyColumnValue, visitFormFields } from '../../../shared/column-write.js'
import { browserTimeZone, storeDateOnly, storeDateTime } from '../../../shared/app-timezone'
import { fieldsAsColumns, recordFailures } from '../../../shared/column-validation.js'

  const inputMap = {
    ...formControlMap,
    formGroup: FormGroup,
    formRow: FormRow
  }

  export default {
    components: {
      ViewTitle,
      FormFieldDescription
    },
    props: ['formComponents', 'uniqueTitle', 'title', 'isEditForm', 'submissionRules', 'defaultValueRules', 'formActions', 'spreadSheetUniqueTitle'],
    setup () {

      const formFormatting = reactive({})
      const formValues = reactive({})
      const formReadonly = reactive({})

      provide('formFormatting', formFormatting)
      provide('formValues', formValues)
      provide('formReadonly', formReadonly)
      provide('updateFormValue', (fieldName, value) => {
        formValues[fieldName] = value
      })

      return { formFormatting, formValues, formReadonly }
    },
    provide () {
      return { relationshipHost: this }
    },
    data: function () {

      return {
        internalFormComponents: [],
        internalIsEditForm: false,
        submittedClass: ``,
        submitText: `submit form`,
        rawFormComponents: null,
        submissionMessage: ``,
        submitError: ``,
        liveValidationError: ``,
        pendingRelationshipDefaults: [],
        pendingRelationshipAdds: {},
        editRecordId: null
      }
    },
    watch: {
      isEditForm: {
        immediate: true,
        async handler () {

          if (!this.isEditForm) {

            return
          }

          if (window.__superturbo_preview_mode) return

          this.internalIsEditForm = true

          console.log(`getting data for editForm!`)

          let url = `${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/pages/${this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle()}/components/${this.uniqueTitle}/accesskey/${this.routeAccessKey}`
          let componentData = await axios.get(url)

          console.log('axios resp')
          console.log(componentData.data)
          console.log(Object.keys(componentData.data))
          if (componentData.data && componentData.data.id != null) this.editRecordId = componentData.data.id

          await nextTick()

          for (const dataPoint of Object.keys(componentData.data)) {

            console.log(`prop: ${dataPoint}, data: ${componentData.data[dataPoint]}`)

            $(`input[name="${dataPoint}"]`).val(componentData.data[dataPoint])

            // Seed form values for reactive formatting evaluation
            this.formValues[dataPoint] = componentData.data[dataPoint]
          }

          this.reEvaluateFormatting()

          // Apply default value rules for edit forms too (readonly states, defaults for empty fields)
          await this.applyDefaultValueRules()

          console.log(`whaaat`)

        }
      },
      formValues: {
        deep: true,
        handler () {

          this.reEvaluateFormatting()
          this.refreshLiveValidation()
        }
      },
      formComponents: {
        immediate: true,
        handler () {

          console.log(`form formComponents handler called!`)
          console.log(this)
          console.log(this.uniqueTitle)
          console.log(this.isEditForm)
          console.log(this.formComponents)

          this.rawFormComponents = this.formComponents
          this.internalFormComponents = []

          let counter = 0

          if (!this.formComponents) {

            return
          }

          for (let component of this.formComponents) {

            console.log(`form adding component ${component.type} with placeholder ${component.placeholder}`)
            console.log(component)

            const controlType = component.type === 'formInput' && component.columnType === 'dateTime'
              ? 'formDate'
              : component.type
            let instantiatedComponent = _.cloneDeep(inputMap[controlType])

            if (!instantiatedComponent) {

              console.warn(`form skipping unknown component type ${component.type}`)
              continue
            }

            instantiatedComponent.uniqueTitle = `${this.uniqueTitle}_${counter++}`

            if (component.uniqueTitle) {

              instantiatedComponent.uniqueTitle = component.uniqueTitle
            }

            applyFormControlProps(instantiatedComponent, component, { spaceDirectInputs: true })

            if (component.components) {

              console.log(`${instantiatedComponent.uniqueTitle} with ${component.components.length} child components`)
              console.log(JSON.stringify(component.components))
            }

            console.log(`push! ${instantiatedComponent.uniqueTitle}`)

            this.internalFormComponents.push(markRaw(instantiatedComponent))

            continue
          }

          // Column defaults first, then form rules that depend on other answers
          if (!this.isEditForm) {
            this.seedColumnDefaults()
            nextTick(() => this.applyDefaultValueRules())
          }
        }
      }
    },
    methods: {
      submissionContext () {

        return { openInModal: !!(this.modalRoute && this.modalRoute.active) }
      },
      withModalQuery (url) {

        if (!this.submissionContext().openInModal) return url

        return `${url}${url.includes('?') ? '&' : '?'}openInModal=1`
      },
      autoCreateChildPageUniqueTitle () {

        const actions = evaluateSubmissionRules(this.submissionRules, this.formValues, this.submissionContext())
        if (!actions) return null

        const childAction = actions.find(action => action.type === 'redirect_child' && action.autoCreate && action.pageUniqueTitle)
        return childAction ? childAction.pageUniqueTitle : null
      },
      refreshLiveValidation () {

        const skip = new Set()

        if (this.internalIsEditForm || this.isEditForm) {

          visitFormFields(this.rawFormComponents, (field) => {

            if (field.type === 'formPassword' || field.type === 'formFile' || field.type === 'formSignature') skip.add(field.uniqueTitle)
          })
        }

        const failures = recordFailures(fieldsAsColumns(this.rawFormComponents), this.formValues, null, { skip })
        const started = Object.keys(this.formValues || {}).some((key) => !isEmptyColumnValue(this.formValues[key]))

        this.liveValidationError = started && failures.length ? failures[0].message : ''
      },
      seedColumnDefaults () {

        visitFormFields(this.rawFormComponents, (field) => {

          if (isEmptyColumnValue(field.defaultValue)) return
          if (!isEmptyColumnValue(this.formValues[field.uniqueTitle])) return

          this.formValues[field.uniqueTitle] = field.defaultValue
        })
      },
      failSubmit (error) {

        this.submittedClass = ''
        this.submitText = 'submit form'
        const data = error && error.response && error.response.data
        this.submitError = typeof data === 'string' && data.trim()
          ? data
          : (data && data.error) || 'Could not save this form.'
      },
      async applyDefaultValueRules () {

        if (!this.defaultValueRules) return
        if (window.__superturbo_preview_mode) return

        const result = evaluateDefaultValueRules(this.defaultValueRules, this.formValues)

        for (const [field, isReadonly] of Object.entries(result.readonly)) {

          this.formReadonly[field] = isReadonly
        }

        await nextTick()

        // Apply static defaults after readonly, so a re-render does not wipe them
        for (const [field, value] of Object.entries(result.defaults)) {

          this.formValues[field] = value

          await nextTick()

          const $input = $(`#${this.uniqueTitle} [name="${field}"]`)
          if ($input.length) {
            $input.val(value)
          }
        }

        // Store relationship defaults to link after submission
        if (result.relationshipDefaults && result.relationshipDefaults.length > 0) {
          this.pendingRelationshipDefaults = result.relationshipDefaults
        }

        // Fetch related record values if any
        if (result.relatedLookups.length > 0) {

          try {

            const account = this.routeAccount
            const application = this.$route.params.application
            const pageUniqueTitle = this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
            const accessKey = this.routeAccessKey

            const response = await axios.post(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUniqueTitle}/components/${this.uniqueTitle}/related-defaults`, {
              lookups: result.relatedLookups,
              accessKey: accessKey || null
            })

            if (response.data && response.data.values) {

              for (const [field, value] of Object.entries(response.data.values)) {

                this.formValues[field] = value

                await nextTick()

                const $input = $(`#${this.uniqueTitle} [name="${field}"]`)
                if ($input.length) {
                  $input.val(value)
                }
              }
            }
          } catch (error) {

            console.log('Error fetching related defaults:', error)
          }
        }

        this.reEvaluateFormatting()
      },
      reEvaluateFormatting () {

        if (!this.rawFormComponents) return

        const newFormatting = evaluateFormFormatting(this.rawFormComponents, this.formValues)

        // Clear old keys
        for (const key of Object.keys(this.formFormatting)) {
          if (!newFormatting[key]) {
            delete this.formFormatting[key]
          }
        }

        // Set new keys
        for (const [key, value] of Object.entries(newFormatting)) {
          this.formFormatting[key] = value
        }
      },
      async onSubmit (event) {

        event.preventDefault()

        if (window.__superturbo_preview_mode) return

        if (this.submittedClass === `disabled`) {

          return
        }

        this.submitError = ''

        console.log('==================for msave')

        let $inputs = $(`#${this.uniqueTitle} :input`)
        let values = {}
        let files = {}

        for (let input of $inputs) {

          console.log(`>>>>`)
          console.log(input)

          const name = $(input).attr(`name`)
          const type = $(input).attr(`type`)

          if (!name) {

            continue
          }

          if (type === `radio` && !input.checked) {

            continue
          }

          values[name] = $(input).val()

          console.log(`val!`)
          console.log(values[name])

          if (type === `file`) {

            const file = $(input)[0].files.item(0)

            values[name] = file || ''

            if (file) files[name] = file.name
            else if (this.formValues[name] && this.formValues[name].url) values[name] = this.formValues[name]
          }
        }

        const skip = new Set()

        if (this.internalIsEditForm) {

          visitFormFields(this.rawFormComponents, (field) => {

            if ((field.type === 'formPassword' || field.type === 'formFile' || field.type === 'formSignature') && isEmptyColumnValue(values[field.uniqueTitle])) {
              skip.add(field.uniqueTitle)
            }
          })
        }

        const failures = recordFailures(fieldsAsColumns(this.rawFormComponents), values, null, { skip })

        if (failures.length) {
          this.submitError = failures[0].message
          return
        }

        this.submittedClass = `disabled`
        this.submitText = `submitted`

        let newData

        console.log(`posting data wiuth new data`)
        console.log(values)

        visitFormFields(this.rawFormComponents, (field) => {

          const dateField = field.type === 'formDate' || (field.type === 'formInput' && field.columnType === 'dateTime')

          if (!dateField || values[field.uniqueTitle] == null || values[field.uniqueTitle] === '') return

          values[field.uniqueTitle] = field.dateOnly
            ? (storeDateOnly(values[field.uniqueTitle]) || values[field.uniqueTitle])
            : (storeDateTime(values[field.uniqueTitle], browserTimeZone()) || values[field.uniqueTitle])
        })

        const data = new FormData()

        for (const key of Object.keys(values)) {

          if (files[key]) {

            data.append(key, values[key], files[key])
            continue
          }

          if (values[key] && typeof values[key] === 'object') {

            data.append(key, JSON.stringify(values[key]))
            continue
          }

          data.append(key, values[key])
        }

        if (this.pendingRelationshipAdds && Object.keys(this.pendingRelationshipAdds).length) {
          data.append('__relationshipAdds', JSON.stringify(this.pendingRelationshipAdds))
        }

        console.log(`our form is`)
        console.log(this.internalIsEditForm)

        if (this.internalIsEditForm) {
     
          try {

            newData = await axios.put(this.withModalQuery(`${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/pages/${pageHostUniqueTitle(this.$el)}/components/${this.uniqueTitle}/accesskey/${this.routeAccessKey}`), data, {
              headers: {
                'Content-Type': 'multipart/form-data',
              }
            })
          } catch (error) {

            console.log(`error creating new data:`)
            console.log(error)
            this.failSubmit(error)
            return
          }
        } else {

          try {

            let createUrl = `${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/pages/${pageHostUniqueTitle(this.$el)}/components/${this.uniqueTitle}`

            const formAccessKey = this.routeAccessKey
            if (formAccessKey) {
              createUrl += `?accessKey=${encodeURIComponent(formAccessKey)}`
            }

            const childPageUniqueTitle = this.autoCreateChildPageUniqueTitle()
            if (childPageUniqueTitle) {
              createUrl += `${createUrl.includes('?') ? '&' : '?'}childPageUniqueTitle=${encodeURIComponent(childPageUniqueTitle)}`
            }

            createUrl = appendLoggedInUserQuery(createUrl, loggedInUserFieldLists(this.pendingRelationshipDefaults))
            createUrl = this.withModalQuery(createUrl)

            newData = await axios.post(createUrl, data, {
              headers: {
                'Content-Type': 'multipart/form-data',
              }
            })
          } catch (error) {

            console.log(`error creating new data:`)
            console.log(error)
            this.failSubmit(error)
            return
          }
        }

        // Link relationship defaults after record creation
        if (newData && newData.data && newData.data.id && this.pendingRelationshipDefaults.length > 0) {

          const recordId = newData.data.id
          const ssTitle = this.spreadSheetUniqueTitle

          for (const relDefault of this.pendingRelationshipDefaults) {

            if (relDefault.loggedInUser) continue

            for (const rec of relDefault.records || []) {

              try {
                await axios.post(`${VUE_APP_PROXY_SERVER_URL}/data/${ssTitle}/${recordId}/link/${relDefault.field}`, {
                  targetId: rec.id
                })
              } catch (linkErr) {
                console.log('Error linking relationship default:', linkErr)
              }
            }
          }
        }

        // Execute form actions (automation execution)
        await this.executeFormActions(newData)

        // Evaluate and execute submission rules
        const actions = evaluateSubmissionRules(this.submissionRules, this.formValues, this.submissionContext())

        if (actions && actions.length > 0) {

          this.executeSubmissionActions(actions, newData)
        }

        return
      },
      async executeFormActions (responseData) {

        if (!this.formActions || !this.formActions.actions || this.formActions.actions.length === 0) return

        for (const action of this.formActions.actions) {

          if (action.stepWorkflowUniqueTitle || action.kind === 'step_workflow') continue

          if (!action.automationUniqueTitle || !action.automationVariables) continue

          // Build variables object from mappings
          const variables = {}

          for (const variable of action.automationVariables) {

            if (variable.source === 'field' && variable.fieldUniqueTitle) {

              variables[variable.variableName] = this.formValues[variable.fieldUniqueTitle] || ''
            } else if (variable.source === 'related' && variable.relatedSpreadsheet && variable.relatedField) {

              // For related fields, try to get the value from the response data
              // The related field value would need to be resolved server-side
              // For now, pass it as a placeholder that the server can resolve
              variables[variable.variableName] = (responseData && responseData.data && responseData.data[variable.relatedField]) || ''
            } else if (variable.source === 'static') {

              variables[variable.variableName] = variable.staticValue || ''
            }
          }

          try {

            const savedRecord = responseData && responseData.data && responseData.data.id
              ? { id: responseData.data.id, spreadSheetUniqueTitle: this.spreadSheetUniqueTitle }
              : null

            await axios.post(`${VUE_APP_PROXY_SERVER_URL}/automations/${action.automationUniqueTitle}/execute`, {
              trigger: 'form_submission',
              variables: variables,
              record: savedRecord
            }, {
              headers: { 'application-unique-title': this.$route.params.application }
            })
          } catch (error) {

            console.log('error executing form action automation:', error)
          }
        }
      },
      prepareFormForAnotherSubmit () {

        this.submittedClass = ''
        this.submitText = 'submit form'
        this.submissionMessage = ''

        for (const key of Object.keys(this.formValues)) {
          this.formValues[key] = ''
        }

        $(`#${this.uniqueTitle} :input`).each(function () {
          const type = $(this).attr('type')
          if (type === 'file') {
            $(this).val('')
          } else if (type !== 'submit' && type !== 'button') {
            $(this).val('')
          }
        })
      },
      executeSubmissionActions (actions, responseData) {

        for (const action of actions) {

          switch (action.type) {

            case 'redirect_parent':
            case 'redirect_page':
            case 'redirect_child': {

              const data = responseData && responseData.data
              const path = submissionRedirectPath(action, {
                application: this.$route.params.application,
                path: this.routePath || this.$route.path,
                accessKey: this.routeAccessKey,
                embed: this.routeEmbed,
                linkId: data && (data.linkId || data.id || data._id)
              })

              if (path) pushAppPath(this.$router, path)

              return
            }

            case 'clear_form': {

              // Reset form state
              this.submittedClass = ''
              this.submitText = 'submit form'

              // Clear all form values
              for (const key of Object.keys(this.formValues)) {
                this.formValues[key] = ''
              }

              // Clear actual input elements
              $(`#${this.uniqueTitle} :input`).each(function () {
                const type = $(this).attr('type')
                if (type === 'file') {
                  $(this).val('')
                } else if (type !== 'submit' && type !== 'button') {
                  $(this).val('')
                }
              })

              // Show custom message if configured
              if (action.showMessage && action.message) {
                this.submissionMessage = action.message
              }

              break
            }

            case 'refresh_views': {

              this.prepareFormForAnotherSubmit()
              window.dispatchEvent(new CustomEvent('superturbo-refresh-views'))
              break
            }

            case 'refresh_view': {

              this.prepareFormForAnotherSubmit()

              if (action.viewUniqueTitle) {

                window.dispatchEvent(new CustomEvent('superturbo-refresh-views', {
                  detail: { viewUniqueTitle: action.viewUniqueTitle }
                }))
              }

              break
            }

            case 'close_modal':
            case 'close_modal_reload': {

              window.dispatchEvent(new CustomEvent('superturbo-close-modal', {
                detail: { reload: action.type === 'close_modal_reload' }
              }))

              break
            }
          }
        }
      }
    },
    mounted () {

      this._onCalendarPrefill = (event) => {

        const detail = event && event.detail

        if (!detail || detail.formUniqueTitle !== this.uniqueTitle) return
        if (this.internalIsEditForm || this.isEditForm) return

        const values = detail.values || {}

        for (const [field, value] of Object.entries(values)) {

          this.formValues[field] = value
        }

        nextTick(() => {

          if (this.$el && this.$el.scrollIntoView) this.$el.scrollIntoView({ block: 'start' })
        })
      }

      window.addEventListener('superturbo-calendar-prefill', this._onCalendarPrefill)
    },
    beforeUnmount () {

      if (this._onCalendarPrefill) window.removeEventListener('superturbo-calendar-prefill', this._onCalendarPrefill)
    }
  }
</script>
